import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';

import { useApprovedPage } from '@/components/approved-content/context';
import type { LinkPair } from '@/utils/approved-content/links';
import { approvedLinkPair, externalHref, homeIntlLinkPair, resolveSiteHref } from '@/utils/approved-content/links';
import type { HomeIntlPage, HomeIntlVersao } from '@/utils/home-intl/types';

/**
 * Home internacional (/{pais}/{idioma}): a página do par entra por este contexto. Fora dele (a home de hoje, nos 10
 * locales do Next) o valor é `null` e todos os ganchos abaixo devolvem exatamente o comportamento de antes — é isso que
 * mantém a produção idêntica com a home internacional desligada.
 */
export const HomeIntlContext = createContext<HomeIntlPage | null>(null);

export function HomeIntlProvider({ page, children }: { page: HomeIntlPage | null; children: ReactNode }) {
  return <HomeIntlContext.Provider value={page}>{children}</HomeIntlContext.Provider>;
}

/** O par da página (null na home dos 10 locales). */
export const useHomeIntl = () => useContext(HomeIntlContext);

/** Prefixo do par (`/br/pt`), ou '' fora da home internacional. */
export const homeIntlPrefix = (page: HomeIntlPage | null) => (page ? page.path : '');

/**
 * Caminho interno da home → caminho no par, sem consultar as páginas publicadas (fora das regras de link do par; o
 * cabeçalho, o rodapé e os CTAs usam useHomeLink). Âncoras (#jogos), URLs absolutas e `//host` não mudam.
 */
export const toHomeIntlHref = (page: HomeIntlPage | null, href: string) => {
  if (!page || !href.startsWith('/') || href.startsWith('//')) return href;
  return href === '/' ? page.path : `${page.path}${href}`;
};

/**
 * Versão da Home nos 10 locales do site (sem par): o preview /preview/home/home-v2…v4 entra por aqui com os textos do
 * idioma (loadSiteHomeVersao). Numa página de par vale a versão do par.
 */
export const HomeVersaoContext = createContext<HomeIntlVersao | null>(null);

export function HomeVersaoProvider({ versao, children }: { versao: HomeIntlVersao | null; children: ReactNode }) {
  return <HomeVersaoContext.Provider value={versao}>{children}</HomeVersaoContext.Provider>;
}

/** A versão da Home desta página (v2|v3|v4 do par ou do locale do site), ou null na v1 e em qualquer outra página. */
export const useHomeVersao = () => {
  const page = useContext(HomeIntlContext);
  const site = useContext(HomeVersaoContext);
  return page?.versao ?? site ?? null;
};

/** Destino do "Planos e preços" do rodapé do par (o mesmo resolvedor dos links do cabeçalho e do rodapé). */
export const homeIntlPricingHref = (page: Pick<HomeIntlPage, 'path' | 'grupo' | 'homeNovaV3'>) => resolveSiteHref('/pricing', homeIntlLinkPair(page));

/**
 * Teste grátis numa página de par: página fora das 29, então vai para a produção no locale mais próximo do idioma. Nas
 * versões "Assine" da Home (`versao.cta === 'assine'`), o CTA de teste vira o de assinatura e vai para o "Planos e preços"
 * do rodapé do par. Sem `versao` (a Home do par e as outras páginas) nada muda.
 */
export const homeIntlTrialHref = (page: Pick<HomeIntlPage, 'path' | 'grupo' | 'homeNovaV3' | 'versao'>) => (page.versao?.cta === 'assine' ? homeIntlPricingHref(page) : externalHref('trial', page.grupo));

/** Contexto de link da página atual: a página aprovada do par (com as páginas publicadas) ou a Home v3 do par. */
export function useLinkPair(): LinkPair | null {
  const page = useHomeIntl();
  const approved = useApprovedPage();
  return useMemo(() => {
    if (approved) return approvedLinkPair(approved);
    return page ? homeIntlLinkPair(page) : null;
  }, [page, approved]);
}

/**
 * Props de next/link para um caminho da home. Numa página de par (Home v3 ou página aprovada) vale o resolvedor único
 * (src/utils/approved-content/links.ts): página das 29 publicada no par → /{pais}/{idioma}/...; das 29 não publicada e
 * qualquer outra página → https://noping.com/{locale}/... (locale de produção mais próximo); âncoras ficam. Fora de um
 * par (a home dos 10 locales), o caminho como estava, e o next/link põe o locale como sempre.
 */
export function useHomeLink() {
  const pair = useLinkPair();
  // prefetch desligado: nada de pré-carregar (páginas de produção são outro domínio).
  return useCallback((href: string) => (pair ? { href: resolveSiteHref(href, pair), locale: false as const, prefetch: false } : { href }), [pair]);
}

/** Língua BCP 47 da página (Intl.NumberFormat, DisplayNames...): a do par, ou `fallback` na home dos 10 locales. */
export function useHomeIntlLang(fallback: string) {
  const page = useHomeIntl();
  return useMemo(() => page?.bcp47 ?? fallback, [page, fallback]);
}
